import { FC, useEffect, useRef, useState } from 'react';
import { beautifyThePrice, isArray, isNotEmpty, isNumber } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { getYouthMapModelClusterMallFlowRankCdnUrl } from '@/common/api/youth';
// import { EnvironmentFilled, EnvironmentOutlined } from '@ant-design/icons';
import { Modal, Spin } from 'antd';
import { FixedSizeList as List } from 'react-window';
import cs from 'classnames';
import styles from './RankModal.module.less';
import dayjs from 'dayjs';
import AutoSizer from 'react-virtualized-auto-sizer';

type FlowType = 'daily' | 'annual';
type RankScope = 'city' | 'nation';
type FlowMetric = 'dailyAvg' | 'workday' | 'holiday';

interface RankRecord {
  placeId: number | string;
  placeName: string;
  rank: number | string;
  flow: number | string;
  previousRank: number | string;
  isTarget: boolean;
}

interface RankModalProps {
  open: boolean;
  detail: any;
  flowType: FlowType;
  latestMonth?: string;
  flowCoefficient: number;
  rankScope: RankScope;
  flowMetric: FlowMetric;
  onCancel: () => void;
}

interface RankDataCache {
  month: string;
  data: any[];
}

interface RankDataRequestCache {
  month: string;
  promise: Promise<any[]>;
}

// 全国排名文件较大，只在页面内存中保留当前月份一份解析后的数据；切换月份时直接替换旧缓存。
let rankDataCache: RankDataCache | null = null;
// 请求缓存用于收口同月份的并发请求，避免快速点击时重复获取和解析同一份大文件。
let rankDataRequestCache: RankDataRequestCache | null = null;

const RANK_FIELD_CONFIG = {
  daily: {
    city: {
      dailyAvg: { rank: 'MCityRank', flow: 'MAvgUv', previousRank: 'prevMCityRank' },
      workday: { rank: 'MCityWorkdayRank', flow: 'MAvgWorkdayUv', previousRank: 'prevMCityWorkdayRank' },
      holiday: { rank: 'MCityHolidayRank', flow: 'MAvgHolidayUv', previousRank: 'prevMCityHolidayRank' },
    },
    nation: {
      dailyAvg: { rank: 'MNationRank', flow: 'MAvgUv', previousRank: 'prevMNationRank' },
      workday: { rank: 'MNationWorkdayRank', flow: 'MAvgWorkdayUv', previousRank: 'prevMNationWorkdayRank' },
      holiday: { rank: 'MNationHolidayRank', flow: 'MAvgHolidayUv', previousRank: 'prevMNationHolidayRank' },
    },
  },
  annual: {
    city: {
      dailyAvg: { rank: 'YCityRank', flow: 'YAvgUv', previousRank: 'prevYCityRank' },
      workday: { rank: 'YCityWorkdayRank', flow: 'YAvgWorkdayUv', previousRank: 'prevYCityWorkdayRank' },
      holiday: { rank: 'YCityHolidayRank', flow: 'YAvgHolidayUv', previousRank: 'prevYCityHolidayRank' },
    },
    nation: {
      dailyAvg: { rank: 'YNationRank', flow: 'YAvgUv', previousRank: 'prevYNationRank' },
      workday: { rank: 'YNationWorkdayRank', flow: 'YAvgWorkdayUv', previousRank: 'prevYNationWorkdayRank' },
      holiday: { rank: 'YNationHolidayRank', flow: 'YAvgHolidayUv', previousRank: 'prevYNationHolidayRank' },
    },
  },
};

/**
 * 商场客流排名详情弹窗。
 * @description 根据点击的排名维度读取全国商场排名文件，并定位当前商场。
 */
const RankModal: FC<RankModalProps> = ({
  open,
  detail,
  flowType,
  latestMonth,
  flowCoefficient,
  rankScope,
  flowMetric,
  onCancel,
}) => {
  const listRef = useRef<any>(null);
  const readyListRef = useRef<any>(null);
  const loadedMonthRef = useRef<string>('');
  // 请求序号用于隔离月份切换和弹窗关闭后的旧请求，避免旧结果覆盖当前状态。
  const requestVersionRef = useRef<number>(0);
  const [allData, setAllData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // refLoaded 表示虚拟列表已经完成至少一轮项目渲染，可以安全调用 scrollToItem。
  const [refLoaded, setRefLoaded] = useState<boolean>(false);
  // 列表重新挂载时递增，确保 Modal 二次打开且 refLoaded 已为 true 时仍会触发定位。
  const [listRenderVersion, setListRenderVersion] = useState<number>(0);
  // 每次打开弹窗递增，使用新的 key 重新挂载列表，避免复用上一次关闭时的滚动状态。
  const [openVersion, setOpenVersion] = useState<number>(0);

  const methods = useMethods({
    fetchRankData(requestVersion) {
      if (!latestMonth) {
        setRefLoaded(false);
        setAllData([]);
        setLoading(false);
        return;
      }
      const month = dayjs(latestMonth).format('YYYYMM');
      const isMonthChanged = loadedMonthRef.current !== month;
      loadedMonthRef.current = month;
      // 命中当前月份缓存时，只复用已经解析好的数组，不再请求 CDN 或执行 JSON 解析。
      if (rankDataCache?.month === month) {
        if (requestVersion !== requestVersionRef.current) {
          return;
        }
        if (isMonthChanged) {
          // 切换月份即使命中缓存，也要等待新排序数据完成一轮列表渲染后再定位。
          setRefLoaded(false);
        }
        setAllData(rankDataCache.data);
        setLoading(false);
        return;
      }
      setLoading(true);
      // 请求新月份数据前先清空列表就绪标识，避免旧列表实例抢先执行定位。
      setRefLoaded(false);
      setAllData([]);
      let requestPromise: Promise<any[]>;
      // 同一月份已有请求时复用 Promise，避免多个弹窗打开事件并行下载 7M-20M 文件。
      if (rankDataRequestCache?.month === month) {
        requestPromise = rankDataRequestCache.promise;
      } else {
        requestPromise = getYouthMapModelClusterMallFlowRankCdnUrl({
          month,
        }).then((res) => {
          const url = res?.url;
          if (!url) {
            return [];
          }
          return fetch(url).then((response) => response.json()).then((data) => (Array.isArray(data) ? data : []));
        });
        rankDataRequestCache = {
          month,
          promise: requestPromise,
        };
      }
      requestPromise.then((data) => {
        // 请求已失效时，旧结果不再回写当前弹窗，也不覆盖新月份缓存。
        if (requestVersion !== requestVersionRef.current) {
          return;
        }
        rankDataCache = { month, data };
        setAllData(isArray(data) ? data : []);
      }).catch((error) => {
        // 失败数据不写入数据缓存，且请求缓存会在 finally 中清理，后续打开时允许重新请求。
        console.log(error);
        if (requestVersion === requestVersionRef.current) {
          setAllData([]);
        }
      }).finally(() => {
        if (requestVersion === requestVersionRef.current) {
          setLoading(false);
        }
        if (rankDataRequestCache?.month === month && rankDataRequestCache.promise === requestPromise) {
          rankDataRequestCache = null;
        }
      });
    },
    getTargetPlaceId() {
      return detail?.resourceSiteId || detail?.servicePlaceId || detail?.placeId || detail?.placeBaseInfo?.placeId;
    },
    buildRankData(data: any[]) {
      const fieldConfig = RANK_FIELD_CONFIG[flowType][rankScope][flowMetric];
      const targetPlaceId = methods.getTargetPlaceId();
      const sourceData = rankScope === 'city'
        ? data.filter((item) => String(item?.cityId) === String(detail?.cityId))
        : data.slice();
      const sortedData = sourceData
        .filter((item) => isNotEmpty(item?.[fieldConfig.rank]))
        .sort((first, second) => Number(first[fieldConfig.rank]) - Number(second[fieldConfig.rank]));
      return sortedData.map((item) => ({
        placeId: item.placeId,
        placeName: item.placeName,
        rank: item[fieldConfig.rank],
        flow: isNotEmpty(item[fieldConfig.flow]) ? Number(item[fieldConfig.flow]) * flowCoefficient : item[fieldConfig.flow],
        previousRank: item[fieldConfig.previousRank],
        isTarget: String(item.placeId) === String(targetPlaceId),
      }));
    },
    formatFlowValue(value) {
      const targetVal = isNumber(value) ? Math.floor(value) : value; // 向下取整
      return isNotEmpty(value) ? `${beautifyThePrice(targetVal, ',', 0)}人次` : '-';
    },
    getPeriodText() {
      return flowType === 'daily'
        ? `${dayjs(latestMonth).format('M')}月`
        // : `${dayjs(latestMonth).format('YYYY')}年`;
        : ``;
    },
    getMetricText() {
      if (flowMetric === 'workday') return '工作日客流';
      if (flowMetric === 'holiday') return '节假日客流';
      return flowType === 'daily' ? '日均客流' : '年均客流';
    },
    getScopeText() {
      return rankScope === 'city' ? `在${detail?.cityName || '-'}所有商场排名` : '在全国所有商场排名';
    },
    renderItem({ index, style, data }) {
      const item: RankRecord = data[index];
      return (
        <div
          className={cs(styles.rankRow, { [styles.targetRow]: item.isTarget })}
          style={style}
        >
          <div className={styles.rankCell}>{item.rank}</div>
          <div className={styles.nameCell}>{item.placeName || '-'}</div>
          <div className={styles.flowCell}>{methods.formatFlowValue(item.flow)}</div>
          {
            flowType === 'daily' && <div className={styles.previousRankCell}>{isNotEmpty(item.previousRank) ? item.previousRank : '-'}</div>
          }
        </div>
      );
    },
    scrollToTarget(index: number) {
      if (index > -1 && listRef.current) {
        listRef.current.scrollToItem(index, 'start');
      }
    },
    handleItemsRendered() {
      // 只有 onItemsRendered 被触发后，才能确认 AutoSizer 已完成尺寸计算且列表实例可定位。
      if (listRef.current && readyListRef.current !== listRef.current) {
        readyListRef.current = listRef.current;
        setListRenderVersion((version) => version + 1);
      }
      setRefLoaded(true);
    },
  });

  useEffect(() => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    if (open) {
      // Modal 本身不销毁内容，通过 key 让列表在每次打开时重新挂载，保留弹窗内容稳定渲染。
      readyListRef.current = null;
      setRefLoaded(false);
      setOpenVersion((version) => version + 1);
      methods.fetchRankData(requestVersion);
    }
  }, [open, latestMonth]);

  const rankData: RankRecord[] = methods.buildRankData(allData);
  const targetIndex = rankData.findIndex((item) => item.isTarget);
  const targetRecord = targetIndex > -1 ? rankData[targetIndex] : undefined;

  useEffect(() => {
    // 必须等待 FixedSizeList 触发 onItemsRendered 后再定位；缓存命中时数据可能先于列表完成挂载。
    if (open && targetIndex > -1 && !loading && refLoaded && listRef.current) {
      methods.scrollToTarget(targetIndex);
    }
  }, [open, targetIndex, loading, refLoaded, listRenderVersion, allData, flowType, rankScope, flowMetric]);

  return (
    <Modal
      open={open}
      title={null}
      footer={null}
      width={760}
      className={styles.rankModal}
      onCancel={onCancel}
    >
      <div className={styles.rankContent}>
        <div className={styles.rankTitle}>
          {/* <EnvironmentFilled className={styles.cityIcon} /> */}
          <span className={styles.mallName}>{targetRecord?.placeName || detail?.areaName || '-'}</span>
          {/* <EnvironmentOutlined className={styles.scopeIcon} /> */}
          <span>{methods.getScopeText()}</span>
          <span className={styles.currentRank}>{targetRecord?.rank || '-'}</span>
        </div>
        <Spin spinning={loading}>
          <div className={styles.rankTable}>
            <div className={styles.rankHeader}>
              <div className={styles.rankCell}>排名</div>
              <div className={styles.nameCell}>商场名称</div>
              <div className={styles.flowCell}>{methods.getPeriodText()}{methods.getMetricText()}值（人次）</div>
              {
                flowType === 'daily' && <div className={styles.previousRankCell}>上月排名</div>
              }
            </div>
            <div className={styles.listViewport}>
              {rankData.length ? (
                <AutoSizer>
                  {({ height, width }) => (
                    <List
                      key={`${openVersion}-${flowType}-${rankScope}-${flowMetric}-${latestMonth || ''}`}
                      ref={listRef}
                      height={height}
                      width={width}
                      itemCount={rankData.length}
                      itemSize={42}
                      itemData={rankData}
                      overscanCount={8}
                      onItemsRendered={methods.handleItemsRendered}
                    >
                      {methods.renderItem}
                    </List>
                  )}
                </AutoSizer>
              ) : (
                <div className={styles.emptyText}>暂无数据</div>
              )}
            </div>
          </div>
        </Spin>
      </div>
    </Modal>
  );
};

export default RankModal;
